import React, { useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Droplet, Sun } from "lucide-react";
import { Helmet } from "react-helmet";

const Process = () => {
  const [loadingStates, setLoadingStates] = useState({});

  const content = [
    {
      title: "Pure Tradition",
      subtitle: "From Our Groves to Your Table",
      description: "Hand-picked olives from centuries-old trees",
    },
    {
      title: "Nature's Gold",
      subtitle: "Extra Virgin Excellence",
      description: "Cold-pressed within hours of harvest",
    },
  ];

  const videos = [
    {
      src: "https://res.cloudinary.com/drvmuda62/video/upload/f_auto,q_auto/v1764526168/drone1_doppkj.mp4",
      contentIndex: 0,
      title: "Solivera Olive Groves - Aerial View",
      description: "Drone footage showcasing Solivera's olive groves in Kairouan, Tunisia - hand-picked olives from centuries-old trees",
    },
    {
      src: "https://res.cloudinary.com/drvmuda62/video/upload/f_auto,q_auto/v1764525910/drone_xwvuhl.mp4",
      contentIndex: 1,
      title: "Solivera Production Process - Cold Pressing",
      description: "Aerial view of Solivera's premium extra virgin olive oil production facility showcasing cold-pressed excellence",
    },
  ];

  const handleVideoLoad = (videoSrc) => {
    setLoadingStates((prev) => ({ ...prev, [videoSrc]: true }));
  };

  // Structured Data for SEO
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.solivera-oil.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Our History & Process",
        item: "https://www.solivera-oil.com/process",
      },
    ],
  };

  const videoObjectSchema = videos.map((video, index) => ({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.title,
    description: video.description,
    contentUrl: video.src,
    uploadDate: "2024-01-01",
    thumbnailUrl: video.src.replace(/\.mp4$/, ".jpg"),
    publisher: {
      "@type": "Organization",
      name: "Solivera",
      logo: {
        "@type": "ImageObject",
        url: "https://www.solivera-oil.com/logosol.svg",
      },
    },
  }));

  return (
    <>
      <Helmet>
        {/* Primary SEO Meta Tags */}
        <title>
          Solivera Production Process - Traditional Olive Oil Making | Our History
        </title>
        <meta
          name="description"
          content="Experience Solivera's traditional olive oil production process through stunning aerial footage. Watch how we hand-pick olives from centuries-old trees and cold-press them within hours for premium extra virgin olive oil excellence in Kairouan, Tunisia."
        />
        <meta
          name="keywords"
          content="Solivera production process, olive oil making process, traditional olive oil production, cold-pressed olive oil, Kairouan olive groves, Tunisian olive oil production, hand-picked olives, extra virgin olive oil process, olive harvesting, olive oil manufacturing, Solivera history"
        />
        <meta name="author" content="Solivera" />
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <link rel="canonical" href="https://www.solivera-oil.com/process" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="geo.region" content="TN-21" />
        <meta name="geo.placename" content="Kairouan, Tunisia" />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Solivera Production Process - Traditional Olive Oil Making"
        />
        <meta
          property="og:description"
          content="Experience Solivera's traditional olive oil production through stunning aerial footage. Hand-picked olives from centuries-old trees, cold-pressed for premium excellence."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.solivera-oil.com/process" />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/drvmuda62/video/upload/f_auto,q_auto/v1764526168/drone1_doppkj.jpg"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Solivera Olive Groves Aerial View - Traditional Olive Oil Production"
        />
        <meta property="og:site_name" content="Solivera" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:video" content={videos[0].src} />
        <meta property="og:video:type" content="video/mp4" />
        <meta property="og:video:width" content="1920" />
        <meta property="og:video:height" content="1080" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="player" />
        <meta
          name="twitter:title"
          content="Solivera Production Process - Traditional Olive Oil Making"
        />
        <meta
          name="twitter:description"
          content="Experience Solivera's traditional olive oil production through stunning aerial footage. Hand-picked olives, cold-pressed excellence."
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/drvmuda62/video/upload/f_auto,q_auto/v1764526168/drone1_doppkj.jpg"
        />
        <meta name="twitter:player" content={videos[0].src} />
        <meta name="twitter:player:width" content="1920" />
        <meta name="twitter:player:height" content="1080" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {videoObjectSchema.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      <main className="bg-black text-white min-h-screen" role="main" aria-label="Solivera olive oil production process">
      {videos.map((video, index) => (
        <section
          key={video.src}
          aria-labelledby={`process-video-${index}`}
          className="relative w-full min-h-[85vh] sm:min-h-screen"
        >
          {/* Loading Spinner */}
          {!loadingStates[video.src] && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black z-30"
              role="status"
              aria-live="polite"
              aria-label="Loading video"
            >
              <div className="text-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"
                  aria-hidden="true"
                />
                <p className="text-white text-lg font-semibold">
                  Loading Experience...
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Preparing your view
                </p>
              </div>
            </div>
          )}

          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={video.src}
            autoPlay
            loop
            muted
            playsInline
            controls={false}
            preload={index === 0 ? "auto" : "metadata"}
            onLoadedData={() => handleVideoLoad(video.src)}
            aria-label={video.description}
            title={video.title}
          />

          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/80" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />

          {/* Animated Decorative Elements */}
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 0.15, x: 0 }}
            transition={{ duration: 2, delay: 0.5 }}
            className="absolute top-20 left-10 hidden lg:block"
          >
            <Leaf className="w-32 h-32 text-green-400 transform rotate-45" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 0.1, y: 0 }}
            transition={{
              duration: 2,
              delay: 1,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="absolute top-40 right-20 hidden lg:block"
          >
            <Sun className="w-24 h-24 text-yellow-400" />
          </motion.div>

          {/* Company Branding - Bottom Left */}
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="absolute bottom-8 left-6 sm:bottom-12 sm:left-12 z-20 max-w-lg"
          >
            {/* Logo/Brand Area */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.6, type: "spring" }}
              className="flex items-center gap-3 mb-4"
            >
              <div className="relative">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-green-600 to-yellow-600 flex items-center justify-center"
                >
                  <Leaf className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </motion.div>
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  className="absolute -top-1 -right-1"
                >
                  <Droplet className="w-4 h-4 text-yellow-400" />
                </motion.div>
              </div>

              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white tracking-wider" style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.8)" }}>
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                  >
                    Solivera
                  </motion.span>
                </h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1 }}
                  className="text-xs text-green-300 tracking-widest"
                  style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.8)" }}
                >
                  PREMIUM OLIVE OIL
                </motion.p>
              </div>
            </motion.div>

            {/* Content Text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="space-y-1 backdrop-blur-sm bg-black/30 p-3 sm:p-4 rounded-lg border-l-4 border-green-500"
            >
              <h2
                id={`process-video-${index}`}
                className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-1"
                style={{ textShadow: "3px 3px 6px rgba(0,0,0,0.9)" }}
              >
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 1.4 }}
                >
                  {content[video.contentIndex].title}
                </motion.span>
              </h2>

              <motion.p
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 1.6 }}
                className="text-sm sm:text-base text-green-300 font-medium"
                style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.8)" }}
              >
                {content[video.contentIndex].subtitle}
              </motion.p>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.8 }}
                className="text-xs sm:text-sm text-gray-200 pt-1"
                style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.9)" }}
              >
                {content[video.contentIndex].description}
              </motion.p>

              {/* Animated Accent Line */}
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.5, delay: 2 }}
                className="h-0.5 bg-gradient-to-r from-green-500 via-yellow-500 to-transparent rounded-full mt-2"
              />
            </motion.div>

            {/* Floating Olive Branch Animation */}
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -right-16 -top-8 opacity-20 hidden sm:block"
            >
              <Leaf className="w-24 h-24 text-green-400" />
            </motion.div>
          </motion.div>

          {/* Top Right Badge */}
       {/* Top Right Badge */}
{/* <motion.div
  initial={{ opacity: 0, scale: 0 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.8, delay: 2.2, type: "spring" }}
  className="absolute top-12 right-6 sm:top-12 sm:right-12 z-20"
>
  <div className="bg-gradient-to-br from-yellow-600 to-green-700 p-3 sm:p-4 rounded-full backdrop-blur-md border-2 border-yellow-400/50 shadow-2xl">
    <motion.div
      animate={{ rotate: [0, 360] }}
      transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
    >
      <Sun className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
    </motion.div>
  </div>
  <motion.p
    initial={{ opacity: 0 }}  
    animate={{ opacity: 1 }}
    transition={{ delay: 2.5 }}
    className="text-center text-xs sm:text-sm text-white font-bold mt-2"
    style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.9)" }}
  >
    100% Natural
  </motion.p>
</motion.div> */}

        </section>
      ))}
      </main>
    </>
  );
};

export default Process;
